import { inject, isRef, ref } from "vue";
import { messages, languageOptions, defaultLang, languageStorageKey } from "./messages";

const resolveKey = (table, key) => {
  if (!table || !key) return undefined;
  return key.split(".").reduce((acc, part) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, part)) {
      return acc[part];
    }
    return undefined;
  }, table);
};

const getInitialLang = () => {
  const stored = typeof window !== "undefined" ? localStorage.getItem(languageStorageKey) : null;
  if (stored && languageOptions.some((opt) => opt.code === stored)) return stored;
  const browser = typeof navigator !== "undefined" ? navigator.language?.slice(0, 2).toLowerCase() : null;
  if (browser && languageOptions.some((opt) => opt.code === browser)) return browser;
  return defaultLang;
};

export const createI18n = () => {
  const activeLang = ref(getInitialLang());

  const setLang = (lang) => {
    if (!lang || !languageOptions.some((opt) => opt.code === lang)) return;
    activeLang.value = lang;
    if (typeof window !== "undefined") {
      localStorage.setItem(languageStorageKey, lang);
    }
  };

  const t = (key, params = {}) => {
    const langTable = messages[activeLang.value] || messages[defaultLang] || {};
    const fallbackTable = messages[defaultLang] || {};
    const value = resolveKey(langTable, key) ?? resolveKey(fallbackTable, key) ?? key;
    return typeof value === "function" ? value(params) : value;
  };

  return { activeLang, setLang, t, languageOptions };
};

export const useI18n = () => {
  const context = inject("i18n", null);
  const fallbackLang = ref(defaultLang);

  const activeLang = context?.activeLang && isRef(context.activeLang) ? context.activeLang : fallbackLang;
  const t = typeof context?.t === "function" ? context.t : (key) => key;
  const setLang = typeof context?.setLang === "function" ? context.setLang : () => {};
  const options = context?.languageOptions || languageOptions;

  return { activeLang, t, setLang, languageOptions: options };
};
